import React, { Component } from "react"
import PropTypes from "prop-types"
import moment from "moment"
import swal from "sweetalert"

import BigCalendar from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css'

import "components/LoadingSpinner"

BigCalendar.momentLocalizer(moment)

export default class Calendar extends Component {
    static propTypes = {
        quorumDataType: PropTypes.number.isRequired,
        resource: PropTypes.object.isRequired,
        uniqueFlvId: PropTypes.string.isRequired,
        uniqueVizSceneId: PropTypes.string.isRequired,
        onlyFields: PropTypes.array,
        onClick: PropTypes.func.isRequired,

        data: PropTypes.object.isRequired,
        registerVisualization: PropTypes.func.isRequired,
        loadVisualizationData: PropTypes.func.isRequired,
        updateExtraVisualizationData: PropTypes.func.isRequired,
        isVisualizationRegistered: PropTypes.bool.isRequired,
        isVisualizationLoading: PropTypes.bool.isRequired,
        currentFilters: PropTypes.object.isRequired,

        calendarDateRange: PropTypes.shape({
            start: PropTypes.object,
            end: PropTypes.object,
        }),

        visualizationFilterValues: PropTypes.object.isRequired,
        visualizationFilterKeys: PropTypes.object.isRequired,
        convertDatumToCalendarObject: PropTypes.func.isRequired,
    }

    componentDidMount() {
        if (!this.props.isVisualizationRegistered) {
            this.props.registerVisualization(this.props.uniqueFlvId, this.props.uniqueVizSceneId)
        }

        this.loadCalendarData()
    }

    componentDidUpdate(oldProps) {
        const filtersChanged = !this.props.currentFilters.equals(oldProps.currentFilters)
        const calendarRangeChanged = !(
            (this.props.calendarDateRange.start.isSame(oldProps.calendarDateRange.start, "day"))
            && (this.props.calendarDateRange.end.isSame(oldProps.calendarDateRange.end, "day"))
        )

        if (filtersChanged || calendarRangeChanged) {
            this.loadCalendarData()
        }

        if (oldProps.isVisualizationLoading && !this.props.isVisualizationLoading) {
            // throwing in some jQuery here because it otherwise seems impossible
            // to add in the hoverscroll. The reason it's here is the calendar events
            // will only show up after loading has finished, and we need to make sure
            // to reapply the hoverscroll to each event object each time.
            $(".rbc-event-content").addClass('hoverscroll')
        }
    }

    loadCalendarData() {
        const startDate = this.props.calendarDateRange.start
        const endDate = this.props.calendarDateRange.end

        // if we have a start date filter or an end date filter active,
        // there's no point in getting data past our specified dates,
        // even if the calendar goes beyond them. Why would you want to go
        // beyond the dates you've filtered?
        const cappedStartDate = this.props.visualizationFilterValues.start > startDate
            ? this.props.visualizationFilterValues.start
            : startDate

        const cappedEndDate = this.props.visualizationFilterValues.end < endDate
            ? this.props.visualizationFilterValues.end
            : endDate

        // modifyDataOnLoad is a callback that will modify the GET request's
        // response.objects; the convertDatumToCalendarObj gets defined in
        // Calendars.js and is distinct to each calendar
        this.props.loadVisualizationData({
            resource: this.props.resource,
            uniqueFlvId: this.props.uniqueFlvId,
            uniqueVizSceneId: this.props.uniqueVizSceneId,
            additionalGraphKeys: {
                [this.props.visualizationFilterKeys.start]: cappedStartDate.format("YYYY-MM-DD"),
                [this.props.visualizationFilterKeys.end]: cappedEndDate.format("YYYY-MM-DD"),
                only_fields: this.props.onlyFields,
                // both these limits are important! First removes the query limit,
                // second overrides the default Tastypie result maximum
                limit: 0,
                max_limit: 0,
                // in the case we need to put any other kwargs into the GET request.
                ...this.props.additionalGraphKeys
            },
            modifyDataOnLoad: (data) => data.map(datum => this.props.convertDatumToCalendarObject(datum))
        })
    }

    onNavigate = (date, view) => {
        // function taken in by the calendar. We're choosing to load the
        // entire month of the date selected, regardless of what view
        // we're in. We can refine this later if need be.
        const newCalendarStartDate = moment(date).startOf('month').startOf('week')
        const newCalendarEndDate = moment(date).endOf('month').endOf('week')

        if (newCalendarStartDate < this.props.calendarDateRange.start || newCalendarEndDate > this.props.calendarDateRange.end) {
            this.props.updateExtraVisualizationData(
                this.props.uniqueFlvId,
                this.props.uniqueVizSceneId,
                {
                    calendarRange: {
                        start: newCalendarStartDate,
                        end: newCalendarEndDate
                    }
                }
            )
        }
    }

    subscribeToCalendar = () => {
        DjangIO.app.userdata.models.SafedSearch
            .objects
            .create({
                user: DjangIO.app.userdata.models.QuorumUser.foreignKey(parseInt(Userdata.id)),
                data_type: this.props.quorumDataType,
                name: "Quorum Calendar",
                filters: [this.props.currentFilters.toJS()]
            })
            .POST()
            .then(savedSearch => {
                let url = `webcal://${window.location.host}/api/search/calendar_download/?reference=${savedSearch.reference_hash}`

                // Save the html into a span, so we can set it as the `content` of the swal
                const span = document.createElement('span');
                span.innerHTML = `We're trying to download the calendar feed to your computer.
                    Your calendar application should open automatically and subscribe to the
                    calendar; however, if that doesn't happen, you can also subscribe manually.
                    Your calendar subscription link is:<br><br>${url}<br><br>You can find
                    instructions on how to add a calendar subscription to Outlook
                    <a target="_BLANK" href="https://support.office.com/en-us/article/
                    Import-or-subscribe-to-a-calendar-in-Outlook-com-or-Outlook-on-the-web-CFF1429C-5AF6-41EC-A5B4-74F2C278E98C">
                    here</a> and to Google Calendar <a href="https://support.google.com/calendar/answer/37100?co=GENIE.Platform%3DDesktop&hl=en">here</a>.`

                swal({
                    icon: "info",
                    title: "Downloading Calendar Feed",
                    content: span
                })

                return window.location.href = url
            })
    }

    renderSubscribeButton() {
        return (
            <a className="btn btn-default-white button-ti calendar-download" onClick={this.subscribeToCalendar}>
                <i className="fa fa-rss" />
                Subscribe
            </a>
        )
    }

    renderLoadingSpinnerOverlay() {
        return this.props.isVisualizationLoading && (
            <div className="calendar-loading-spinner">
                <LoadingSpinner id="calendar-spinner" />
            </div>
        )
    }

    render() {
        return (
            <div className="quorum-calendar">
                { this.renderSubscribeButton() }
                <div className="qcal-body">
                    { this.renderLoadingSpinnerOverlay() }
                    <BigCalendar
                        events={this.props.data.toJS()}
                        onNavigate={this.onNavigate}
                        onSelectEvent={this.props.onClick}
                        min={moment().set("hour", 5).set("minute", 0).toDate()}
                        eventPropGetter={this.props.eventPropGetter}
                    />
                </div>
            </div>
        )
    }
}
